import React, { useEffect, useState } from 'react';
import jm, { formatDateTime, photoUrl, referredBy } from '../api';
import CandidateTable, { NameCell, RowActions, StatusBadge } from '../common/CandidateTable';
import Modal from '../common/Modal';
import RatingRow from '../common/RatingRow';
import CandidateSummary, {
  SectionHeader,
} from '../common/CandidateSummary';
import useStage from '../common/useStage';
import { EmployeeSelect, useMasters } from '../common/MastersContext';
import DateField, { MONTH_END, YEAR_START } from '../common/DateField';

const NEXT_STATUS = [
  { value: 'Recruited', label: 'Final Interview (stay here)' },
  { value: 'Recruitment', label: 'Recruitment' },
  { value: 'Hold', label: 'Hold' },
  { value: 'Rejected', label: 'Rejected' },
];

const RATINGS = [
  { name: 'grooming', label: 'Grooming' },
  { name: 'body_language', label: 'Body Language' },
  { name: 'ability_learn', label: 'Ability to Learn' },
  { name: 'communication', label: 'Communication' },
  { name: 'team_player', label: 'Team Player' },
  { name: 'Responsible', label: 'Responsible' },
  { name: 'Dedicated', label: 'Dedicated' },
];

const FinalInterview = ({ onMoved }) => {
  const masters = useMasters();
  const [form, setForm] = useState({});

  const { rows, loading, editing, detail, detailLoading, openEdit, closeEdit, saving, save } =
    useStage(jm.final, jm.saveFinal, { onMoved });

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    if (!detail) return;
    const stage = detail.final || {};

    setForm({
      grooming: Number(stage.grooming) || 0,
      body_language: Number(stage.body_language) || 0,
      ability_learn: Number(stage.ability_learn) || 0,
      communication: Number(stage.communication) || 0,
      team_player: Number(stage.team_player) || 0,
      Responsible: Number(stage.Responsible) || 0,
      Dedicated: Number(stage.Dedicated) || 0,
      notes: stage.notes || '',
      job_status: 'Recruited',
      recruit_by: '',
      recruitment_date: '',
      reject_notes: '',
    });
  }, [detail]);

  const liveScore = RATINGS.reduce((sum, field) => sum + (Number(form[field.name]) || 0), 0);

  const columns = [
    { key: 'action', label: 'Action', search: false, sortable: false, render: (row) => <RowActions row={row} onEdit={openEdit} /> },
    { key: 'job_title', label: 'Job Title' },
    { key: 'first_name', label: 'Candidate Name', render: (row) => <NameCell row={row} /> },
    { key: 'Referred_others', label: 'Referred By', render: referredBy, sortValue: referredBy },
    { key: 'email', label: 'Email' },
    { key: 'status', label: 'Status', search: false, sortable: false, render: () => <StatusBadge label="Final Interview" /> },
    { key: 'done_on', label: 'Moved On', render: (row) => formatDateTime(row.done_on) },
  ];

  return (
    <>
      <CandidateTable
        title="Final Interview"
        rows={rows}
        columns={columns}
        loading={loading}
        exportName="final_interview"
      />

      <Modal
        show={!!editing}
        title={editing ? `${editing.first_name} ${editing.last_name || ''}` : ''}
        subtitle={editing ? editing.job_title : ''}
        photo={photoUrl(editing?.emp_img)}
        onClose={closeEdit}
        onSubmit={() => save(form)}
        saving={saving}
      >
        {detailLoading && <div className="text-center py-4">Loading…</div>}

        {!detailLoading && detail && (
          <>
            <CandidateSummary candidate={detail.application} />

            <div className="border rounded p-3 mb-3">
              <SectionHeader title="Final Interview" score={liveScore} max={35} />

              {RATINGS.map((field) => (
                <RatingRow key={field.name} label={field.label} name={field.name} value={form[field.name]} onChange={set} />
              ))}

              <div className="form-group mt-2">
                <label>Remark</label>
                <textarea className="form-control" rows="2" value={form.notes || ''} onChange={(e) => set('notes', e.target.value)} />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 form-group required-tooltip">
                <label>Job Status</label>
                <select className="form-control required-input" value={form.job_status || ''} onChange={(e) => set('job_status', e.target.value)}>
                  {NEXT_STATUS.map((status) => (
                    <option key={status.value} value={status.value}>{status.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {form.job_status === 'Recruitment' && (
              <div className="row">
                <div className="col-md-6 form-group">
                  <label>Recruit By</label>
                  <EmployeeSelect value={form.recruit_by} onChange={(v) => set('recruit_by', v)} employees={masters.employees} />
                </div>
                <div className="col-md-6 form-group">
                  <label>Recruitment Date</label>
                  <DateField
                    value={form.recruitment_date || ''}
                    onChange={(v) => set('recruitment_date', v)}
                    minDate={YEAR_START}
                    maxDate={MONTH_END}
                  />
                </div>
              </div>
            )}

            {form.job_status === 'Rejected' && (
              <div className="form-group">
                <label>Reject Notes</label>
                <textarea className="form-control" rows="2" value={form.reject_notes || ''} onChange={(e) => set('reject_notes', e.target.value)} />
              </div>
            )}
          </>
        )}
      </Modal>
    </>
  );
};

export default FinalInterview;
